/**
 * Premium Responsive Modal Component
 * 
 * Beautiful animated modal with glassmorphism effects.
 * - Full-screen sheet on mobile
 * - Centered dialog on desktop
 */

import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useResponsive } from '../../../hooks';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  /** When false, ESC, overlay click, and close button are ignored. Default true. */
  dismissible?: boolean;
  /** Force full-screen mode regardless of screen size */
  forceFullScreen?: boolean;
}

const sizeClasses = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  full: 'sm:max-w-[90vw] lg:max-w-6xl',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  size = 'md',
  showCloseButton = true,
  dismissible = true,
  forceFullScreen = false,
}) => {
  const { isMobile } = useResponsive();
  const isFullScreen = forceFullScreen || isMobile;
  const canDismiss = dismissible && showCloseButton;

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.paddingRight = `${scrollbarWidth}px`;

      return () => {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
      };
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen || !dismissible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, dismissible]);

  if (!isOpen) return null;

  // Mobile: Slide up from bottom
  const mobileVariants = {
    initial: { y: '100%', opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: '100%', opacity: 0 },
  };

  // Desktop: Scale and fade
  const desktopVariants = {
    initial: { scale: 0.95, opacity: 0, y: 20 },
    animate: { scale: 1, opacity: 1, y: 0 },
    exit: { scale: 0.95, opacity: 0, y: 20 },
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`
            fixed inset-0 z-[9999]
            flex items-end sm:items-center justify-center
            ${isFullScreen ? '' : 'p-0 sm:p-4'}
          `}
          onClick={(e) => {
            if (dismissible && e.target === e.currentTarget) onClose();
          }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-none"
          />

          {/* Modal Content */}
          <motion.div
            variants={isFullScreen ? mobileVariants : desktopVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className={`
              relative z-10 w-full
              ${isFullScreen
                ? 'h-[95vh] sm:h-auto rounded-t-3xl sm:rounded-2xl'
                : `${sizeClasses[size]} rounded-2xl`
              }
              bg-white
              shadow-2xl shadow-black/20
              overflow-hidden
              flex flex-col
            `}
          >
            {/* Drag Handle (mobile only) */}
            {isFullScreen && (
              <div className="flex justify-center pt-3 pb-1 sm:hidden">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
              </div>
            )}

            {/* Header */}
            <div className={`
              relative
              px-4 sm:px-6 py-4 sm:py-5
              bg-gradient-to-r from-gray-50 to-white
              border-b border-gray-100
              flex-shrink-0
            `}>
              {/* Close Button */}
              {canDismiss && (
                <button
                  onClick={onClose}
                  className="
                    absolute top-3 sm:top-4 right-3 sm:right-4
                    w-9 h-9 sm:w-8 sm:h-8 rounded-xl sm:rounded-lg
                    flex items-center justify-center
                    text-gray-400 hover:text-gray-600
                    bg-gray-100 sm:bg-transparent
                    hover:bg-gray-100
                    transition-colors
                  "
                >
                  <X size={18} />
                </button>
              )}

              {/* Title with Icon */}
              <div className={`flex items-center gap-3 sm:gap-4 ${canDismiss ? 'pr-10 sm:pr-8' : ''}`}>
                {icon && (
                  <div className="
                    w-10 h-10 sm:w-12 sm:h-12 rounded-xl
                    bg-gradient-to-br from-primary-500 to-primary-700
                    flex items-center justify-center flex-shrink-0
                    shadow-lg shadow-primary-500/20
                    text-white
                  ">
                    {React.cloneElement(icon as React.ReactElement, {
                      size: isMobile ? 20 : 24,
                    })}
                  </div>
                )}
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{title}</h2>
                  {subtitle && (
                    <p className={`text-xs sm:text-sm text-gray-500 mt-0.5 ${canDismiss ? 'truncate' : ''}`}>{subtitle}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={`
              px-4 sm:px-6 py-4 sm:py-5
              flex-1 overflow-y-auto
              ${isFullScreen ? 'max-h-none' : 'max-h-[70vh]'}
            `}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default Modal;


